import { Bloc, delay } from "@kernel/frontend-react-app";
import { RoomState, defaultRoomState } from "./RoomState";
import { RoomBloc } from "./RoomBloc";
import { StatsRecordingService } from "./services";
import { Room, StatsRecorder } from "@domain";
import { MainBloc } from "modules/main";

export type RoomStatsBlocState = Pick<
  RoomState,
  "isStatsRecording" | "statsRecorder" | "report"
>;

export class RoomStatsBloc extends Bloc<RoomStatsBlocState> {
  private statsService: StatsRecordingService | null = null;
  private mainBloc: MainBloc | null = null;
  public constructor() {
    super({
      statsRecorder: defaultRoomState.statsRecorder,
      report: defaultRoomState.report,
      isStatsRecording: defaultRoomState.isStatsRecording
    });
  }

  public setMainBloc(mainBloc: MainBloc): void {
    this.mainBloc = mainBloc;
  }

  public getMainBloc(): MainBloc {
    if (this.mainBloc === null) throw new Error("MainBloc is null");
    return this.mainBloc;
  }

  public startLoading(message: string): void {
    const mainBloc = this.getMainBloc();
    mainBloc.startLoading(message);
  }

  public stopLoading(): void {
    const mainBloc = this.getMainBloc();
    mainBloc.stopLoading();
  }

  public getStatsService(): StatsRecordingService {
    if (this.statsService === null) throw new Error("StatsService is null");
    return this.statsService;
  }

  public start(room: Room): void {
    const statsRecorder = StatsRecorder.create({
      roomName: room.name,
      date: new Date()
    });
    this.statsService = new StatsRecordingService(statsRecorder);
    const participants = room.getRemoteParticipants();
    if (participants.getItems().length === 0) {
      console.warn("No participants to record stats");
      return;
    }
    this.statsService.start(participants);
    this.changeState({
      ...this.state,
      statsRecorder,
      isStatsRecording: true
    });
  }

  public stop(): void {
    const statsService = this.getStatsService();
    statsService.stop();
    this.changeState({
      ...this.state,
      isStatsRecording: false,
      report: statsService.report
    });
  }

  public download(type: "json" | "csv"): void {
    const statsService = this.getStatsService();
    if (type === "json") {
      statsService.exportToJson();
    } else {
      statsService.exportToCsv();
    }
    statsService.removeStats();
  }

  public async upload(): Promise<void> {
    const statsService = this.getStatsService();
    const storedReport = statsService.report;
    if (storedReport === null) {
      console.warn("No stats to upload");
      return;
    }
    this.getMainBloc().startLoading("Salvataggio preventivo");
    try {
      await delay(500);
      await statsService.saveStats();
      statsService.removeStats();
    } catch (err) {
      console.error(err);
      this.getMainBloc().handleError(err);
    } finally {
      this.getMainBloc().stopLoading();
    }
  }

  public static create(parent: RoomBloc): RoomStatsBloc {
    const bloc = new RoomStatsBloc();
    bloc.subscribe((state: RoomStatsBlocState) => {
      parent.changeState({
        ...parent.state,
        isStatsRecording: state.isStatsRecording
      });
    });
    return bloc;
  }
}
